import { useClientEmployeesQuery } from "@graphql/client-employees/clientEmployees.g";
import {
  TenantedClientApplicationManagedByChoices,
  TenantedClientApplicationReviewStateChoices,
  TenantedClientApplicationSupportedByChoices,
} from "@graphql/generated/graphql.g";
import { ComponentProps, useMemo, forwardRef } from "react";
import { SubmitHandler, useForm } from "react-hook-form";

import { Button } from "@design-system/Button/Button";
import { FieldGroup } from "@design-system/FieldGroup/FieldGroup";
import { Form } from "@design-system/Form/Form";
import { Select } from "@design-system/Select/Select";
import { MultiLineTextField } from "@design-system/MultiLineTextField/MultiLineTextField";

export type ClientApplicationApprovalReviewFormValues = {
  reviewState: TenantedClientApplicationReviewStateChoices;
  managedBy?: TenantedClientApplicationManagedByChoices | null;
  businessOwnerId?: string | null;
  supportedBy?: TenantedClientApplicationSupportedByChoices | null;
  clientId: string;
  notes?: string | null;
};

type Props = {
  submitText?: string;
  onSubmit: SubmitHandler<ClientApplicationApprovalReviewFormValues>;
  values: ClientApplicationApprovalReviewFormValues;
  errorsAPI?: ComponentProps<typeof Form>["errorsAPI"];
  disabled?: boolean;
  withSubmitButton?: boolean;
};

export const ClientApplicationApprovalReviewForm = forwardRef<HTMLFormElement, Props>(({
  submitText = "Submit",
  onSubmit,
  values,
  errorsAPI,
  withSubmitButton = true,
}, ref) => {
  const {
    handleSubmit,
    formState: { isSubmitting },
    setValue,
    watch,
    register,
    formState: { isDirty },
  } = useForm<ClientApplicationApprovalReviewFormValues>({
    values: {
      reviewState:
        values?.reviewState ??
        TenantedClientApplicationReviewStateChoices.Discovered,
      managedBy: values.managedBy ?? undefined,
      supportedBy: values.supportedBy ?? undefined,
      businessOwnerId: values.businessOwnerId ?? undefined,
      clientId: values.clientId,
      notes: values.notes ?? undefined,
    },
  });

  const APPROVAL_OPTIONS: Array<{
    id: TenantedClientApplicationReviewStateChoices;
    label: string;
  }> = [
    {
      id: TenantedClientApplicationReviewStateChoices.Discovered,
      label: `Discovered`,
    },
    {
      id: TenantedClientApplicationReviewStateChoices.Evaluating,
      label: `Being evaluated`,
    },
    {
      id: TenantedClientApplicationReviewStateChoices.Approved,
      label: `Approved`,
    },
    {
      id: TenantedClientApplicationReviewStateChoices.Unapproved,
      label: `Not approved`,
    },
  ];

  const SUPPORTED_BY_OPTIONS: Array<{
    id: TenantedClientApplicationSupportedByChoices;
    label: string;
  }> = [
    {
      id: TenantedClientApplicationSupportedByChoices.Msp,
      label: `MSP`,
    },
    {
      id: TenantedClientApplicationSupportedByChoices.Vendor,
      label: `Vendor`,
    },
    {
      id: TenantedClientApplicationSupportedByChoices.Client,
      label: `Client`,
    },
  ];

  const MANAGED_BY_OPTIONS: Array<{
    id: TenantedClientApplicationManagedByChoices;
    label: string;
  }> = [
    {
      id: TenantedClientApplicationManagedByChoices.Msp,
      label: `MSP`,
    },
    {
      id: TenantedClientApplicationManagedByChoices.Vendor,
      label: `Vendor`,
    },
    {
      id: TenantedClientApplicationManagedByChoices.Client,
      label: `Client`,
    },
  ];

  const clientEmployeesQuery = useClientEmployeesQuery({
    clientId: values.clientId,
    orderBy: "name",
  });

  const CLIENT_EMPLOYEE_OPTIONS = useMemo(() => {
    return (
      clientEmployeesQuery.data?.clientEmployees?.edges
        ?.map((edge) => edge?.node)
        ?.filter((clientEmployee) => !!clientEmployee)
        ?.map((employee) => ({
          id: employee.id,
          label: `${employee.name} (${employee.email})`,
        })) || []
    );
  }, [clientEmployeesQuery.data]);

  const reviewState = watch("reviewState");
  const managedBy = watch("managedBy");
  const supportedBy = watch("supportedBy");
  const businessOwnerId = watch("businessOwnerId");

  const createHandleChange = (
    fieldName: keyof ClientApplicationApprovalReviewFormValues,
  ) => {
    return (value: string | string[]) => {
      const enumValue = Array.isArray(value) ? value[0] : value;
      setValue(fieldName, enumValue as never, { shouldDirty: true });
    };
  };

  return (
    <Form ref={ref} onSubmit={handleSubmit(onSubmit)} errorsAPI={errorsAPI}>
      <FieldGroup label="Review state">
        <Select
          options={APPROVAL_OPTIONS}
          value={reviewState}
          onChange={createHandleChange("reviewState")}
        />
      </FieldGroup>

      <FieldGroup label="Managed by">
        <Select
          options={MANAGED_BY_OPTIONS}
          value={managedBy ?? ""}
          onChange={createHandleChange("managedBy")}
        />
      </FieldGroup>

      <FieldGroup label="Supported by">
        <Select
          options={SUPPORTED_BY_OPTIONS}
          value={supportedBy ?? ""}
          onChange={createHandleChange("supportedBy")}
        />
      </FieldGroup>

      <FieldGroup label="Business owner">
        <Select
          options={CLIENT_EMPLOYEE_OPTIONS}
          value={businessOwnerId ?? ""}
          onChange={createHandleChange("businessOwnerId")}
        />
      </FieldGroup>

      <FieldGroup label="Notes">
        <MultiLineTextField {...register("notes")} />
      </FieldGroup>

      {withSubmitButton && (
        <Button isLoading={isSubmitting} disabled={!isDirty} type="submit">
          {submitText}
        </Button>
      )}
    </Form>
  );
});

ClientApplicationApprovalReviewForm.displayName = "ClientApplicationApprovalReviewForm";
